import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Settings, Flame, Zap, Target, Users, Trash2, Gift, 
  TrendingUp, Shield, Save, RefreshCw, ChevronLeft,
  Coins, Percent, Timer, Award, Crown, BarChart3,
  AlertTriangle, Check, Rocket, DollarSign, Lock, Wallet,
  Github, Download, Database, History, Terminal,
  CheckCircle, XCircle, Clock, HardDrive, FileText,
  Plus, X, Send, Trophy, Palette, ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BURNZ_LOGO, API } from "../constants";
import { DEMO } from "../constants/mockData";
import { ThemeSelector } from "../components/ThemeSelector";
import VerificationQueue from "../components/VerificationQueue";
import { BurnTracker } from "../components/features/BurnTracker";

// Campaign Types for Web2-friendly Quest System
const CAMPAIGN_TYPES = [
  { id: 'share', label: 'Share', icon: '📢', description: 'Share content on social media', color: 'blue' },
  { id: 'visit', label: 'Visit', icon: '🔗', description: 'Visit a website or page', color: 'green' },
  { id: 'follow', label: 'Follow', icon: '👥', description: 'Follow on social platforms', color: 'purple' },
  { id: 'review', label: 'Review', icon: '⭐', description: 'Leave a review or feedback', color: 'yellow' },
  { id: 'referral', label: 'Referral', icon: '🎁', description: 'Refer new users', color: 'orange' },
];

// Team Management Section Component
const TeamManagementSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [teams, setTeams] = useState(() => {
    const stored = localStorage.getItem('burnz_admin_teams');
    if (stored) {
      try { return JSON.parse(stored); } catch { return []; }
    }
    return [
      { id: 'team_1', name: 'Phoenix Rising', emoji: '🔥', theme: 'fire', member_count: 1247, total_xp: 2450000, status: 'active' },
      { id: 'team_2', name: 'Diamond Hands', emoji: '💎', theme: 'ice', member_count: 892, total_xp: 1890000, status: 'active' },
      { id: 'team_3', name: 'Moon Walkers', emoji: '🌙', theme: 'space', member_count: 756, total_xp: 1560000, status: 'active' },
    ];
  });
  const [teamForm, setTeamForm] = useState({
    name: '',
    emoji: '🔥',
    theme: 'fire',
    description: '',
    max_members: 5000,
  });

  const THEMES = [
    { id: 'fire', label: 'Fire', color: 'bg-gradient-to-br from-orange-500 to-red-600' },
    { id: 'ice', label: 'Ice', color: 'bg-gradient-to-br from-cyan-400 to-blue-600' },
    { id: 'space', label: 'Space', color: 'bg-gradient-to-br from-purple-500 to-indigo-600' },
    { id: 'nature', label: 'Nature', color: 'bg-gradient-to-br from-green-400 to-emerald-600' },
    { id: 'gold', label: 'Gold', color: 'bg-gradient-to-br from-yellow-400 to-amber-600' },
  ];

  const saveTeams = (newTeams) => {
    setTeams(newTeams);
    localStorage.setItem('burnz_admin_teams', JSON.stringify(newTeams));
  };

  const handleCreateTeam = async () => {
    if (!teamForm.name) {
      alert('Please enter a team name');
      return;
    }

    setCreating(true);
    try {
      const newTeam = {
        id: `team_${Date.now()}`,
        ...teamForm,
        member_count: 0,
        total_xp: 0,
        status: 'active',
        created_at: new Date().toISOString(),
      };
      
      saveTeams([...teams, newTeam]);
      setShowForm(false);
      setTeamForm({ name: '', emoji: '🔥', theme: 'fire', description: '', max_members: 5000 });
      alert('Team Card created successfully!');
    } catch (error) {
      alert('Failed to create team: ' + error.message);
    }
    setCreating(false);
  };

  const toggleTeamStatus = (teamId) => {
    const updated = teams.map(team => 
      team.id === teamId ? { ...team, status: team.status === 'active' ? 'inactive' : 'active' } : team
    );
    saveTeams(updated);
  };

  const deleteTeam = (teamId) => {
    if (confirm('Are you sure you want to delete this team?')) {
      saveTeams(teams.filter(team => team.id !== teamId));
    }
  };

  return (
    <Card className="bg-[#1A1A1A] border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Team Cards Management
            </CardTitle>
            <CardDescription className="text-white/50">
              Create and manage Team Cards for user collaboration
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className={showForm ? "bg-white/10 hover:bg-white/20 text-white" : "bg-purple-500 hover:bg-purple-600 text-white"}
            data-testid="toggle-team-form-btn"
          >
            {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {showForm ? 'Cancel' : 'New Team'}
          </Button>
        </div>
      </CardHeader>

      {showForm && (
        <CardContent className="space-y-4 border-t border-white/10 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Team Name *</label>
              <Input
                placeholder="e.g., Phoenix Rising"
                value={teamForm.name}
                onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="team-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Team Emoji</label>
              <Input
                placeholder="🔥"
                value={teamForm.emoji}
                onChange={(e) => setTeamForm({...teamForm, emoji: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Theme</label>
              <select
                value={teamForm.theme}
                onChange={(e) => setTeamForm({...teamForm, theme: e.target.value})}
                className="w-full h-10 px-3 bg-[#0D0D0D] border border-white/10 text-white rounded-md"
              >
                {THEMES.map(theme => (
                  <option key={theme.id} value={theme.id}>{theme.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Description</label>
              <Textarea
                placeholder="A brief description of the team..."
                value={teamForm.description}
                onChange={(e) => setTeamForm({...teamForm, description: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white min-h-[60px]"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Max Members</label>
              <Input
                type="number"
                value={teamForm.max_members}
                onChange={(e) => setTeamForm({...teamForm, max_members: parseInt(e.target.value) || 5000})}
                className="bg-[#0D0D0D] border-white/10 text-white"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-white/10 text-white/50">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTeam}
              disabled={creating}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="create-team-btn"
            >
              {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {creating ? 'Creating...' : 'Create Team'}
            </Button>
          </div>
        </CardContent>
      )}

      {/* Active Teams List */}
      <CardContent className="border-t border-white/10 pt-4">
        <h4 className="text-white/70 text-sm font-medium mb-3">Active Team Cards ({teams.filter(t => t.status === 'active').length})</h4>
        <div className="space-y-2">
          {teams.map((team) => {
            const theme = THEMES.find(t => t.id === team.theme) || THEMES[0];
            return (
              <div key={team.id} className={`flex items-center justify-between p-3 rounded-lg border ${team.status === 'active' ? 'border-purple-500/30 bg-purple-500/5' : 'border-white/10 bg-white/5'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${theme.color} flex items-center justify-center text-xl`}>
                    {team.emoji}
                  </div>
                  <div>
                    <p className="text-white font-medium">{team.name}</p>
                    <div className="flex items-center gap-3 text-xs text-white/50">
                      <span>{team.member_count.toLocaleString()} members</span>
                      <span>•</span>
                      <span>{(team.total_xp / 1000).toFixed(0)}K XP</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleTeamStatus(team.id)}
                    className={`h-7 text-xs ${team.status === 'active' ? 'border-green-500/30 text-green-400' : 'border-white/10 text-white/50'}`}
                  >
                    {team.status === 'active' ? 'Active' : 'Inactive'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteTeam(team.id)}
                    className="h-7 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Campaign Creation Section Component (Replaces old Quest Creation)
const CampaignCreationSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    title: '',
    description: '',
    campaign_type: 'share',
    xp_reward: 500,
    target_url: '',
    instructions: '',
    max_completions: 1000,
  });

  const handleCreateCampaign = async () => {
    if (!campaignForm.title || !campaignForm.description) {
      alert('Please fill in campaign title and description');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          ...campaignForm,
          status: 'active',
        })
      });

      if (response.ok) {
        alert('Campaign created successfully!');
        setShowForm(false);
        setCampaignForm({
          title: '',
          description: '',
          campaign_type: 'share',
          xp_reward: 500,
          target_url: '',
          instructions: '',
          max_completions: 1000,
        });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create campaign');
      }
    } catch (error) {
      alert('Failed to create campaign: ' + error.message);
    }
    setCreating(false);
  };

  const selectedType = CAMPAIGN_TYPES.find(t => t.id === campaignForm.campaign_type) || CAMPAIGN_TYPES[0];

  return (
    <Card className="bg-[#1A1A1A] border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Create Marketing Campaign
            </CardTitle>
            <CardDescription className="text-white/50">
              Create Web2-friendly marketing quests for partners
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className={showForm ? "bg-white/10 hover:bg-white/20 text-white" : "bg-green-500 hover:bg-green-600 text-white"}
            data-testid="toggle-campaign-form-btn"
          >
            {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {showForm ? 'Cancel' : 'New Campaign'}
          </Button>
        </div>
      </CardHeader>

      {showForm && (
        <CardContent className="space-y-4 border-t border-white/10 pt-4">
          {/* Campaign Type Selection */}
          <div>
            <label className="text-sm text-white/70 block mb-2">Campaign Type *</label>
            <div className="grid grid-cols-5 gap-2">
              {CAMPAIGN_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setCampaignForm({...campaignForm, campaign_type: type.id})}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    campaignForm.campaign_type === type.id 
                      ? `border-${type.color}-500 bg-${type.color}-500/20` 
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <span className="text-2xl block mb-1">{type.icon}</span>
                  <span className="text-xs text-white font-medium">{type.label}</span>
                </button>
              ))}
            </div>
            <p className="text-white/40 text-xs mt-2">{selectedType.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Campaign Title *</label>
              <Input
                placeholder="e.g., Share our launch tweet"
                value={campaignForm.title}
                onChange={(e) => setCampaignForm({...campaignForm, title: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="campaign-title-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Description *</label>
              <Textarea
                placeholder="Describe what users need to do to complete this campaign..."
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white min-h-[80px]"
                data-testid="campaign-description-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Target URL</label>
              <Input
                placeholder="https://twitter.com/yourproject/status/..."
                value={campaignForm.target_url}
                onChange={(e) => setCampaignForm({...campaignForm, target_url: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
              />
              <p className="text-white/30 text-xs mt-1">The link users will interact with</p>
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">XP Reward</label>
              <Input
                type="number"
                value={campaignForm.xp_reward}
                onChange={(e) => setCampaignForm({...campaignForm, xp_reward: parseInt(e.target.value) || 0})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="campaign-xp-input"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Max Completions</label>
              <Input
                type="number"
                value={campaignForm.max_completions}
                onChange={(e) => setCampaignForm({...campaignForm, max_completions: parseInt(e.target.value) || 1000})}
                className="bg-[#0D0D0D] border-white/10 text-white"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Instructions (Optional)</label>
              <Textarea
                placeholder="Step-by-step instructions for users..."
                value={campaignForm.instructions}
                onChange={(e) => setCampaignForm({...campaignForm, instructions: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white min-h-[60px]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-white/10 text-white/50">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCampaign}
              disabled={creating}
              className="bg-green-500 hover:bg-green-600 text-white"
              data-testid="create-campaign-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Campaign
                </>
              )}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Legacy Quest Creation Section (kept for backwards compatibility)
const QuestCreationSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [questForm, setQuestForm] = useState({
    title: '',
    description: '',
    quest_type: 'social',
    xp_reward: 500,
    burnz_reward: 50,
    pool_id: '44444444-4444-4444-4444-444444444444', // Default to BURNZ Official pool
  });
  const [tasks, setTasks] = useState([
    { title: '', description: '', xp_reward: 100, action_url: '', task_type: 'custom' }
  ]);

  const addTask = () => {
    setTasks([...tasks, { title: '', description: '', xp_reward: 100, action_url: '', task_type: 'custom' }]);
  };

  const removeTask = (index) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const updateTask = (index, field, value) => {
    const newTasks = [...tasks];
    newTasks[index][field] = value;
    setTasks(newTasks);
  };

  const handleCreateQuest = async () => {
    if (!questForm.title || !questForm.description) {
      alert('Please fill in quest title and description');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/quests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          ...questForm,
          token_reward: questForm.burnz_reward, // Map burnz_reward to token_reward for API
          tasks: tasks.filter(t => t.title).map((t, i) => ({
            ...t,
            task_order: i,
            target_url: t.action_url, // Map action_url to target_url for API
            verification_type: 'self_report'
          }))
        })
      });

      if (response.ok) {
        alert('Quest created successfully!');
        setShowForm(false);
        setQuestForm({
          title: '',
          description: '',
          quest_type: 'social',
          xp_reward: 500,
          burnz_reward: 50,
          pool_id: '44444444-4444-4444-4444-444444444444',
        });
        setTasks([{ title: '', description: '', xp_reward: 100, action_url: '', task_type: 'custom' }]);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create quest');
      }
    } catch (error) {
      alert('Failed to create quest: ' + error.message);
    }
    setCreating(false);
  };

  return (
    <Card className="bg-[#1A1A1A] border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Create New Quest
            </CardTitle>
            <CardDescription className="text-white/50">
              Create a new quest with tasks for users to complete
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className={showForm ? "bg-white/10 hover:bg-white/20 text-white" : "bg-green-500 hover:bg-green-600 text-white"}
            data-testid="toggle-quest-form-btn"
          >
            {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {showForm ? 'Cancel' : 'New Quest'}
          </Button>
        </div>
      </CardHeader>

      {showForm && (
        <CardContent className="space-y-4 border-t border-white/10 pt-4">
          {/* Quest Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Quest Title *</label>
              <Input
                placeholder="e.g., Follow @BurnzStudio on Twitter"
                value={questForm.title}
                onChange={(e) => setQuestForm({...questForm, title: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="quest-title-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Description *</label>
              <Textarea
                placeholder="Describe what users need to do..."
                value={questForm.description}
                onChange={(e) => setQuestForm({...questForm, description: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white min-h-[80px]"
                data-testid="quest-description-input"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Quest Type</label>
              <select
                value={questForm.quest_type}
                onChange={(e) => setQuestForm({...questForm, quest_type: e.target.value})}
                className="w-full h-10 px-3 bg-[#0D0D0D] border border-white/10 text-white rounded-md"
                data-testid="quest-type-select"
              >
                <option value="social">Social</option>
                <option value="action">Action</option>
                <option value="onboarding">Onboarding</option>
                <option value="community">Community</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Pool</label>
              <select
                value={questForm.pool_id}
                onChange={(e) => setQuestForm({...questForm, pool_id: e.target.value})}
                className="w-full h-10 px-3 bg-[#0D0D0D] border border-white/10 text-white rounded-md"
                data-testid="quest-pool-select"
              >
                <option value="44444444-4444-4444-4444-444444444444">BURNZ Official</option>
                <option value="11111111-1111-1111-1111-111111111111">Neon Apes Genesis</option>
                <option value="22222222-2222-2222-2222-222222222222">Cyber Punks CNF</option>
                <option value="33333333-3333-3333-3333-333333333333">Meta Monkeys DAO</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">XP Reward</label>
              <Input
                type="number"
                value={questForm.xp_reward}
                onChange={(e) => setQuestForm({...questForm, xp_reward: parseInt(e.target.value) || 0})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="quest-xp-input"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">BURNZ Reward</label>
              <Input
                type="number"
                value={questForm.burnz_reward}
                onChange={(e) => setQuestForm({...questForm, burnz_reward: parseInt(e.target.value) || 0})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="quest-burnz-input"
              />
            </div>
          </div>

          {/* Tasks Section */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-white/70 font-medium">Quest Tasks</label>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={addTask}
                className="border-white/10 text-white/50 hover:text-white"
                data-testid="add-task-btn"
              >
                <Plus className="w-3 h-3 mr-1" />Add Task
              </Button>
            </div>
            
            <div className="space-y-3">
              {tasks.map((task, index) => (
                <div key={index} className="p-3 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/50 text-xs">Task {index + 1}</span>
                    {tasks.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeTask(index)}
                        className="h-6 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Task title"
                      value={task.title}
                      onChange={(e) => updateTask(index, 'title', e.target.value)}
                      className="bg-[#0D0D0D] border-white/10 text-white text-sm h-9"
                      data-testid={`task-${index}-title-input`}
                    />
                    <Input
                      placeholder="Action URL (optional)"
                      value={task.action_url}
                      onChange={(e) => updateTask(index, 'action_url', e.target.value)}
                      className="bg-[#0D0D0D] border-white/10 text-white text-sm h-9"
                      data-testid={`task-${index}-url-input`}
                    />
                    <Input
                      placeholder="Description"
                      value={task.description}
                      onChange={(e) => updateTask(index, 'description', e.target.value)}
                      className="bg-[#0D0D0D] border-white/10 text-white text-sm h-9"
                      data-testid={`task-${index}-description-input`}
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="XP"
                        value={task.xp_reward}
                        onChange={(e) => updateTask(index, 'xp_reward', parseInt(e.target.value) || 0)}
                        className="bg-[#0D0D0D] border-white/10 text-white text-sm h-9 w-20"
                        data-testid={`task-${index}-xp-input`}
                      />
                      <span className="text-white/30 text-xs">XP</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button 
              variant="outline" 
              onClick={() => setShowForm(false)}
              className="border-white/10 text-white/50"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateQuest}
              disabled={creating}
              className="bg-green-500 hover:bg-green-600 text-white"
              data-testid="create-quest-btn"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Quest
                </>
              )}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// FireBoard Advertising Management Section
const FireBoardAdsSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [ads, setAds] = useState(() => {
    const stored = localStorage.getItem('burnz_fireboard_ads');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });
  const [adForm, setAdForm] = useState({
    type: 'image',
    title: '',
    description: '',
    media_url: '',
    thumbnail_url: '',
    link_url: '',
    tier: 'standard',
    advertiser: '',
    active: true
  });

  const AD_TIERS = {
    standard: { name: "Standard", price: "500K", burnz: 500000, duration: "1 week", color: "bg-gray-500" },
    featured: { name: "Featured", price: "1M", burnz: 1000000, duration: "2 weeks", color: "bg-orange-500" },
    premium: { name: "Premium", price: "2.5M", burnz: 2500000, duration: "1 month", color: "bg-purple-500" }
  };

  const saveAds = (newAds) => {
    setAds(newAds);
    localStorage.setItem('burnz_fireboard_ads', JSON.stringify(newAds));
  };

  const handleAddAd = () => {
    if (!adForm.title || !adForm.media_url) {
      alert('Please fill in title and media URL');
      return;
    }
    
    const newAd = {
      ...adForm,
      id: `ad_${Date.now()}`,
      created_at: new Date().toISOString()
    };
    
    saveAds([...ads, newAd]);
    setAdForm({
      type: 'image',
      title: '',
      description: '',
      media_url: '',
      thumbnail_url: '',
      link_url: '',
      tier: 'standard',
      advertiser: '',
      active: true
    });
    setShowForm(false);
    alert('Ad added successfully!');
  };

  const toggleAdStatus = (adId) => {
    const updated = ads.map(ad => 
      ad.id === adId ? { ...ad, active: !ad.active } : ad
    );
    saveAds(updated);
  };

  const deleteAd = (adId) => {
    if (confirm('Are you sure you want to delete this ad?')) {
      saveAds(ads.filter(ad => ad.id !== adId));
    }
  };

  return (
    <Card className="bg-[#1A1A1A] border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              FireBoard Advertising
            </CardTitle>
            <CardDescription className="text-white/50">
              Manage slideshow ads displayed on the dashboard
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className={showForm ? "bg-white/10 hover:bg-white/20 text-white" : "bg-orange-500 hover:bg-orange-600 text-white"}
            data-testid="toggle-ad-form-btn"
          >
            {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {showForm ? 'Cancel' : 'New Ad'}
          </Button>
        </div>
      </CardHeader>

      {/* Pricing Tiers Display */}
      <CardContent className="border-t border-white/10 pt-4">
        <div className="mb-4">
          <h4 className="text-white/70 text-sm font-medium mb-3">Advertising Tiers</h4>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(AD_TIERS).map(([key, tier]) => (
              <div key={key} className={`p-3 rounded-lg border ${
                key === 'premium' ? 'border-purple-500/30 bg-purple-500/10' :
                key === 'featured' ? 'border-orange-500/30 bg-orange-500/10' :
                'border-white/10 bg-white/5'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2 h-2 rounded-full ${tier.color}`} />
                  <span className="text-white font-medium text-sm">{tier.name}</span>
                </div>
                <p className="text-orange-400 font-bold">{tier.price} BURNZ</p>
                <p className="text-white/40 text-xs">{tier.duration}</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>

      {/* Add New Ad Form */}
      {showForm && (
        <CardContent className="space-y-4 border-t border-white/10 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Ad Title *</label>
              <Input
                placeholder="e.g., UEX Exchange - Trade Now"
                value={adForm.title}
                onChange={(e) => setAdForm({...adForm, title: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="ad-title-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">Description</label>
              <Textarea
                placeholder="Short description of the ad..."
                value={adForm.description}
                onChange={(e) => setAdForm({...adForm, description: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white min-h-[60px]"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Media Type</label>
              <select
                value={adForm.type}
                onChange={(e) => setAdForm({...adForm, type: e.target.value})}
                className="w-full h-10 px-3 bg-[#0D0D0D] border border-white/10 text-white rounded-md"
              >
                <option value="image">Image</option>
                <option value="video">Video (YouTube/Embed)</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Tier</label>
              <select
                value={adForm.tier}
                onChange={(e) => setAdForm({...adForm, tier: e.target.value})}
                className="w-full h-10 px-3 bg-[#0D0D0D] border border-white/10 text-white rounded-md"
                data-testid="ad-tier-select"
              >
                <option value="standard">Standard (500K BURNZ)</option>
                <option value="featured">Featured (1M BURNZ)</option>
                <option value="premium">Premium (2.5M BURNZ)</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-sm text-white/70 block mb-1">
                {adForm.type === 'video' ? 'Video Embed URL *' : 'Image URL *'}
              </label>
              <Input
                placeholder={adForm.type === 'video' ? 'https://www.youtube.com/embed/...' : 'https://example.com/image.jpg'}
                value={adForm.media_url}
                onChange={(e) => setAdForm({...adForm, media_url: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
                data-testid="ad-media-url-input"
              />
              <p className="text-white/30 text-xs mt-1">
                {adForm.type === 'video' ? 'Use YouTube embed URL format' : 'Direct link to image (recommended 800x400px)'}
              </p>
            </div>
            {adForm.type === 'video' && (
              <div className="col-span-2">
                <label className="text-sm text-white/70 block mb-1">Video Thumbnail URL</label>
                <Input
                  placeholder="https://example.com/thumbnail.jpg"
                  value={adForm.thumbnail_url}
                  onChange={(e) => setAdForm({...adForm, thumbnail_url: e.target.value})}
                  className="bg-[#0D0D0D] border-white/10 text-white"
                />
              </div>
            )}
            <div>
              <label className="text-sm text-white/70 block mb-1">Click-through URL</label>
              <Input
                placeholder="https://yourproject.com"
                value={adForm.link_url}
                onChange={(e) => setAdForm({...adForm, link_url: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 block mb-1">Advertiser Name</label>
              <Input
                placeholder="Company/Project name"
                value={adForm.advertiser}
                onChange={(e) => setAdForm({...adForm, advertiser: e.target.value})}
                className="bg-[#0D0D0D] border-white/10 text-white"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button 
              variant="outline" 
              onClick={() => setShowForm(false)}
              className="border-white/10 text-white/50"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddAd}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="create-ad-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Advertisement
            </Button>
          </div>
        </CardContent>
      )}

      {/* Active Ads List */}
      {ads.length > 0 && (
        <CardContent className="border-t border-white/10 pt-4">
          <h4 className="text-white/70 text-sm font-medium mb-3">Active Advertisements ({ads.filter(a => a.active).length}/{ads.length})</h4>
          <div className="space-y-2">
            {ads.map((ad) => (
              <div key={ad.id} className={`flex items-center justify-between p-3 rounded-lg border ${ad.active ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 bg-white/5'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-10 rounded bg-white/10 overflow-hidden flex-shrink-0">
                    <img 
                      src={ad.type === 'video' ? ad.thumbnail_url || ad.media_url : ad.media_url} 
                      alt={ad.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/160x100?text=Ad'; }}
                    />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{ad.title}</p>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[9px] border-0 ${
                        ad.tier === 'premium' ? 'bg-purple-500/20 text-purple-400' :
                        ad.tier === 'featured' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>{AD_TIERS[ad.tier]?.name}</Badge>
                      <span className="text-white/40 text-xs">{ad.type}</span>
                      <span className="text-white/30 text-xs">by {ad.advertiser}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleAdStatus(ad.id)}
                    className={`h-7 text-xs ${ad.active ? 'border-green-500/30 text-green-400' : 'border-white/10 text-white/50'}`}
                  >
                    {ad.active ? 'Active' : 'Paused'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteAd(ad.id)}
                    className="h-7 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}

      {ads.length === 0 && !showForm && (
        <CardContent className="border-t border-white/10 pt-4">
          <div className="text-center py-6">
            <Flame className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 text-sm">No advertisements yet</p>
            <p className="text-white/30 text-xs">Click &quot;New Ad&quot; to create your first FireBoard advertisement</p>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Partner Applications Management Section
const PartnerApplicationsSection = () => {
  const [applications, setApplications] = useState(() => {
    // Load all applications from localStorage (would be API in production)
    const stored = localStorage.getItem('burnz_all_partner_applications');
    if (stored) {
      try { return JSON.parse(stored); } catch { return []; }
    }
    // Demo applications
    return [
      { id: 'app_1', companyName: 'CryptoVerse', contactEmail: 'team@cryptoverse.io', status: 'pending', submitted_at: '2026-01-18T10:30:00Z', selected_tier: 'growth' },
      { id: 'app_2', companyName: 'NFT Studio Pro', contactEmail: 'hello@nftstudiopro.com', status: 'under_review', submitted_at: '2026-01-17T14:20:00Z', selected_tier: 'enterprise' },
      { id: 'app_3', companyName: 'DeFi Labs', contactEmail: 'contact@defilabs.xyz', status: 'approved', submitted_at: '2026-01-15T09:00:00Z', selected_tier: 'starter' },
    ];
  });

  const statusColors = {
    pending: 'yellow',
    under_review: 'blue', 
    approved: 'green',
    rejected: 'red',
    payment_pending: 'orange',
    active: 'green'
  };

  const handleApprove = (appId) => {
    const updated = applications.map(app => 
      app.id === appId ? { ...app, status: 'approved', approved_at: new Date().toISOString() } : app
    );
    setApplications(updated);
    localStorage.setItem('burnz_all_partner_applications', JSON.stringify(updated));
    alert('Application approved! Partner will be notified to complete payment.');
  };

  const handleReject = (appId) => {
    if (confirm('Are you sure you want to reject this application?')) {
      const updated = applications.map(app => 
        app.id === appId ? { ...app, status: 'rejected', rejected_at: new Date().toISOString() } : app
      );
      setApplications(updated);
      localStorage.setItem('burnz_all_partner_applications', JSON.stringify(updated));
    }
  };

  return (
    <Card className="bg-[#1A1A1A] border-white/10">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-400" />
          Partner Applications
        </CardTitle>
        <CardDescription className="text-white/50">
          Review and manage Campaign Studio partner applications
        </CardDescription>
      </CardHeader>
      <CardContent>
        {applications.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="text-white/40">No pending applications</p>
          </div>
        ) : (
          <div className="space-y-3">
            {applications.map((app) => (
              <div key={app.id} className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{app.companyName}</h4>
                    <p className="text-white/50 text-xs">{app.contactEmail}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`bg-${statusColors[app.status]}-500/20 text-${statusColors[app.status]}-400 border-0 text-[10px]`}>
                        {app.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <span className="text-white/30 text-xs">
                        {new Date(app.submitted_at).toLocaleDateString()}
                      </span>
                      <span className="text-white/30 text-xs">•</span>
                      <span className="text-purple-400 text-xs capitalize">{app.selected_tier} Plan</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {app.status === 'pending' || app.status === 'under_review' ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={() => handleReject(app.id)}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 bg-green-500 hover:bg-green-600 text-white"
                        onClick={() => handleApprove(app.id)}
                      >
                        <Check className="w-3 h-3 mr-1" />Approve
                      </Button>
                    </>
                  ) : (
                    <Badge className={`bg-${statusColors[app.status]}-500/20 text-${statusColors[app.status]}-400 border-0`}>
                      {app.status === 'approved' ? 'Awaiting Payment' : app.status.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// User Roles Management Section
const UserRolesSection = () => {
  const ROLES = [
    { id: 'super_admin', name: 'Super Admin', icon: '👑', color: 'red', bgColor: 'bg-red-500/20', description: 'Full system access - Can do everything including managing other admins', permissions: ['All permissions', 'Manage roles', 'System settings', 'Payments'] },
    { id: 'admin', name: 'Admin', icon: '🛡️', color: 'purple', bgColor: 'bg-purple-500/20', description: 'System administration - Manage users, quests, teams', permissions: ['Manage users', 'Manage quests', 'Manage teams', 'View analytics', 'Ban users'] },
    { id: 'manager', name: 'Manager', icon: '📊', color: 'blue', bgColor: 'bg-blue-500/20', description: 'Team & quest management - Approve partners, manage campaigns', permissions: ['Approve partners', 'Manage quests', 'Manage ads', 'View analytics', 'Create announcements'] },
    { id: 'staff', name: 'Staff', icon: '🎧', color: 'cyan', bgColor: 'bg-cyan-500/20', description: 'Support & moderation - Handle user issues, verify quests', permissions: ['Verify quests', 'View users', 'View analytics', 'Moderate chat'] },
    { id: 'partner', name: 'Partner', icon: '🤝', color: 'orange', bgColor: 'bg-orange-500/20', description: 'Business partner - Create and manage own quest campaigns', permissions: ['Create own quests', 'View own analytics'] },
    { id: 'user', name: 'User', icon: '👤', color: 'gray', bgColor: 'bg-white/10', description: 'Standard user - Complete quests, earn XP, join teams', permissions: ['Complete quests', 'Join teams', 'Earn XP'] },
  ];

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [superAdminWallets, setSuperAdminWallets] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/admin/users?limit=100`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        // Use mock data if API fails
        setUsers([
          { id: 'u1', wallet_address: 'addr1q9ux67lgxrejk752rtwr9gkm24lrthr3td7k7yexq7ms59tqrg557stwp8hwd5eclku7t6wq46gc72na8fwgj22c8e5qurvush', wallet_preview: 'addr1q9ux67l...e5qurvush', role: 'super_admin', is_admin: true, is_superadmin: true, contribution_xp: 50000, contribution_level: 10 },
          { id: 'u2', wallet_address: 'addr1qxc6u2d5ppeuhg7tkj5quf3csxp2022g98az65hpad9m6yreuxes6p3hhg880tegsg95xqwek6srgac5jq04x9rcm2qq6a6md7', wallet_preview: 'addr1qxc6u2d...qq6a6md7', role: 'super_admin', is_admin: true, is_superadmin: true, contribution_xp: 45000, contribution_level: 9 },
        ]);
      }
    } catch (error) {
    }
    setLoading(false);
  };

  // Fetch super admin wallets
  const fetchSuperAdminWallets = async () => {
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/admin/super-admins`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setSuperAdminWallets(data.super_admin_wallets || []);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSuperAdminWallets();
  }, []);

  // Update user role
  const handleUpdateRole = async () => {
    if (!editingUser || !newRole) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/admin/users/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          wallet_address: editingUser.wallet_address,
          role: newRole
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setUsers(users.map(u => 
          u.id === editingUser.id 
            ? { ...u, role: newRole, is_admin: ['admin', 'super_admin', 'manager'].includes(newRole) }
            : u
        ));
        setShowRoleModal(false);
        setEditingUser(null);
        setNewRole('');
        alert(`Role updated successfully: ${data.message}`);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to update role'}`);
      }
    } catch (error) {
      alert('Failed to update role. Please try again.');
    }
    setSaving(false);
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || 
      user.wallet_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.wallet_preview?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const openRoleModal = (user) => {
    setEditingUser(user);
    setNewRole(user.role || 'user');
    setShowRoleModal(true);
  };

  return (
    <>
      {/* Super Admin Wallets */}
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-400" />
            Hardcoded Super Admins
          </CardTitle>
          <CardDescription className="text-white/50">These wallet addresses have permanent super admin access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {superAdminWallets.length > 0 ? superAdminWallets.map((wallet, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <code className="text-yellow-400 text-xs font-mono flex-1 break-all">{wallet}</code>
              </div>
            )) : (
              <p className="text-white/50 text-sm">Loading super admin wallets...</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Roles Overview */}
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-lg">Role Definitions</CardTitle>
          <CardDescription className="text-white/50">Available roles and their permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {ROLES.map((role) => (
              <div key={role.id} className={`p-4 rounded-xl border border-${role.color}-500/20 bg-${role.color}-500/5`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{role.icon}</span>
                  <div>
                    <h4 className="text-white font-bold">{role.name}</h4>
                    <p className="text-white/50 text-xs">{role.description}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {role.permissions.map((perm, i) => (
                    <Badge key={i} className="bg-white/10 text-white/70 border-0 text-[10px]">{perm}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                All Users ({users.length})
              </CardTitle>
              <CardDescription className="text-white/50">Manage user roles and permissions</CardDescription>
            </div>
            <Button 
              variant="outline"
              className="border-white/10 text-white/70 hover:bg-white/10"
              onClick={fetchUsers}
              data-testid="refresh-users-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Search by wallet address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white min-w-[150px]"
            >
              <option value="all">All Roles</option>
              {ROLES.map(role => (
                <option key={role.id} value={role.id}>{role.icon} {role.name}</option>
              ))}
            </select>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-white/30 animate-spin mx-auto mb-2" />
              <p className="text-white/50">Loading users...</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-white/50 text-center py-8">No users found</p>
              ) : filteredUsers.map((user) => {
                const roleInfo = ROLES.find(r => r.id === user.role) || ROLES.find(r => r.id === 'user');
                const isSuperAdmin = user.is_superadmin || superAdminWallets.includes(user.wallet_address);
                
                return (
                  <div key={user.id} className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0 ${isSuperAdmin ? 'bg-gradient-to-br from-yellow-500 to-orange-500' : 'bg-gradient-to-br from-purple-500 to-blue-500'}`}>
                        {isSuperAdmin ? '👑' : roleInfo?.icon || '👤'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <code className="text-white text-sm font-mono truncate">{user.wallet_preview || user.wallet_address?.slice(0, 20) + '...'}</code>
                          {isSuperAdmin && <Badge className="bg-yellow-500/20 text-yellow-400 border-0 text-[10px]">SUPER</Badge>}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-white/50 text-xs">Level {user.contribution_level || 1}</span>
                          <span className="text-white/30">•</span>
                          <span className="text-white/50 text-xs">{(user.contribution_xp || 0).toLocaleString()} XP</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`bg-${roleInfo?.color || 'gray'}-500/20 text-${roleInfo?.color || 'gray'}-400 border-0`}>
                        {roleInfo?.icon} {roleInfo?.name || 'User'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        onClick={() => openRoleModal(user)}
                        data-testid={`edit-role-${user.id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Role Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User Role</DialogTitle>
            <DialogDescription className="text-white/50">
              Change the role for this user
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="text-white/50 text-xs mb-1">Wallet Address</p>
                <code className="text-white text-sm font-mono break-all">{editingUser.wallet_address}</code>
              </div>
              
              <div>
                <label className="text-white/70 text-sm block mb-2">Current Role</label>
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-white">{ROLES.find(r => r.id === editingUser.role)?.icon} {ROLES.find(r => r.id === editingUser.role)?.name || 'User'}</span>
                </div>
              </div>
              
              <div>
                <label className="text-white/70 text-sm block mb-2">New Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-md text-white"
                >
                  {ROLES.map(role => (
                    <option key={role.id} value={role.id}>{role.icon} {role.name}</option>
                  ))}
                </select>
                <p className="text-white/40 text-xs mt-2">
                  {ROLES.find(r => r.id === newRole)?.description}
                </p>
              </div>
              
              {superAdminWallets.includes(editingUser.wallet_address) && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-yellow-400 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    This wallet is a hardcoded super admin. Role changes won't remove super admin access.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1 border-white/10 text-white/50" onClick={() => setShowRoleModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white" 
                  onClick={handleUpdateRole}
                  disabled={saving || newRole === editingUser.role}
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Default ecosystem settings
const DEFAULT_SETTINGS = {
  // Token Economics
  xpToBurnzRatio: 10,           // XP ÷ ratio = BURNZ
  minClaimThreshold: 100,        // Minimum BURNZ to claim
  claimBurnFee: 2,              // % burned on claims
  
  // Dump Zone
  xpPerDump: 100,               // XP earned per NFT dump
  burnzBurnPerDump: 5,          // BURNZ burned per dump
  
  // Quest Rewards
  questXpMultiplier: 1.0,       // Multiplier for quest XP
  questBurnzMultiplier: 1.0,    // Multiplier for quest BURNZ
  dailyQuestLimit: 4,           // Max daily quests per user
  
  // Partner Settings
  partnerFeeBurnPercent: 10,    // % of partner fees burned
  selfServePriceAda: 50,        // ADA price for self-serve
  selfServePriceBurnz: 500,     // BURNZ price for self-serve
  managedServicePrice: 200,     // ADA price for managed
  featuredPlacementCost: 100,   // BURNZ for featured
  
  // Milestones
  milestones: {
    bronze: { xp: 10000, uex: 100, uexc: 500 },
    silver: { xp: 50000, uex: 500, uexc: 2500 },
    gold: { xp: 100000, uex: 1000, uexc: 5000 },
    diamond: { xp: 500000, uex: 5000, uexc: 25000 },
    legendary: { xp: 1000000, uex: 10000, uexc: 50000 },
  },
  
  // Platform Limits
  maxNftsPerDump: 50,           // Max NFTs per single dump
  vestingPeriodMonths: 12,      // UEX vesting period
  energyRegenRate: 10,          // Energy per hour
  maxEnergy: 200,               // Maximum energy
};

const SettingCard = ({ icon: Icon, title, description, children, color = "purple" }) => (
  <Card className="bg-[#1A1A1A] border-white/10 hover:border-white/20 transition-all">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        <div>
          <CardTitle className="text-white text-sm">{title}</CardTitle>
          <CardDescription className="text-white/40 text-xs">{description}</CardDescription>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      {children}
    </CardContent>
  </Card>
);

const StatCard = ({ label, value, icon: Icon, color, change }) => (
  <div className="bg-[#0D0D0D] rounded-xl p-4 border border-white/5">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-5 h-5 text-${color}-400`} />
      {change && <span className={`text-xs ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>{change > 0 ? '+' : ''}{change}%</span>}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-white/50">{label}</p>
  </div>
);

const InputField = ({ label, value, onChange, suffix, min, max, step = 1 }) => (
  <div className="flex items-center justify-between gap-4">
    <label className="text-sm text-white/70 flex-shrink-0">{label}</label>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
        className="w-24 bg-[#0D0D0D] border-white/10 text-white text-right h-9"
      />
      {suffix && <span className="text-white/50 text-sm w-12">{suffix}</span>}
    </div>
  </div>
);

export const CommandCenter = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [activeTab, setActiveTab] = useState("overview");
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // System tab state
  const [versionInfo, setVersionInfo] = useState({
    version: "1.0.0",
    commit: "abc1234",
    branch: "main",
    lastUpdate: new Date().toISOString(),
    repo: "https://github.com/ocratz/Burnz-Union"
  });
  const [updating, setUpdating] = useState(false);
  const [updateLog, setUpdateLog] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const [backups, setBackups] = useState([
    { id: 1, name: "backup_2025-01-18_12-00.sql", size: "2.4 MB", date: "2025-01-18 12:00:00", tables: 8 },
    { id: 2, name: "backup_2025-01-17_12-00.sql", size: "2.3 MB", date: "2025-01-17 12:00:00", tables: 8 },
  ]);
  const [changelog, setChangelog] = useState([
    { version: "1.0.0", date: "2025-01-18", changes: ["Initial release", "Command Center added", "Three-tier pricing", "5000 XP for UEX signup"] },
    { version: "0.9.0", date: "2025-01-17", changes: ["Guest mode implemented", "Landing page redesign", "Dump Zone overhaul"] },
    { version: "0.8.0", date: "2025-01-16", changes: ["Partner portal added", "Profile page with connections", "Exchange integration"] },
  ]);
  
  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    referral_xp_reward: 250,
    nft_dump_xp_per_nft: 100,
    nft_dump_min_ada: 2,
    partner_pricing: {
      starter: { ada: 500, burnz: 500000 },
      growth: { ada: 1500, burnz: 1500000 },
      enterprise: { ada: 5000, burnz: 5000000 }
    }
  });
  const [walletConfig, setWalletConfig] = useState({
    nft_dump_wallet: '',
    partner_payment_wallet: ''
  });
  const [savingSystemSettings, setSavingSystemSettings] = useState(false);

  // Fetch system settings
  const fetchSystemSettings = async () => {
    try {
      const response = await fetch(`${API}/system/settings`);
      if (response.ok) {
        const data = await response.json();
        if (data.settings) setSystemSettings(data.settings);
        if (data.wallets) setWalletConfig(data.wallets);
      }
    } catch (error) {
    }
  };

  const saveSystemSettings = async () => {
    setSavingSystemSettings(true);
    try {
      const response = await fetch(`${API}/system/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referral_xp_reward: systemSettings.referral_xp_reward,
          nft_dump_xp_per_nft: systemSettings.nft_dump_xp_per_nft,
          nft_dump_min_ada: systemSettings.nft_dump_min_ada,
          partner_pricing_starter_ada: systemSettings.partner_pricing?.starter?.ada,
          partner_pricing_growth_ada: systemSettings.partner_pricing?.growth?.ada,
          partner_pricing_enterprise_ada: systemSettings.partner_pricing?.enterprise?.ada,
        })
      });
      if (response.ok) {
        alert('System settings saved successfully!');
      }
    } catch (error) {
      alert('Failed to save settings');
    }
    setSavingSystemSettings(false);
  };

  // Fetch version info on mount
  useEffect(() => {
    fetchVersionInfo();
    fetchSystemSettings();
    fetchBackups();
    fetchRuggedProjects();
  }, []);

  // Rugged Projects State
  const [ruggedProjects, setRuggedProjects] = useState([]);
  const [loadingRugged, setLoadingRugged] = useState(false);
  const [showAddRuggedModal, setShowAddRuggedModal] = useState(false);
  const [newRuggedProject, setNewRuggedProject] = useState({
    policy_id: '',
    name: '',
    description: '',
    image_url: '',
    rug_date: '',
    estimated_holders: ''
  });

  const fetchRuggedProjects = async () => {
    setLoadingRugged(true);
    try {
      const response = await fetch(`${API}/rugged-projects`);
      if (response.ok) {
        const data = await response.json();
        setRuggedProjects(data.projects || []);
      }
    } catch (error) {
    }
    setLoadingRugged(false);
  };

  const addRuggedProject = async () => {
    if (!newRuggedProject.policy_id || !newRuggedProject.name) {
      alert('Policy ID and Name are required');
      return;
    }
    
    try {
      const response = await fetch(`${API}/rugged-projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newRuggedProject,
          estimated_holders: newRuggedProject.estimated_holders ? parseInt(newRuggedProject.estimated_holders) : null
        })
      });
      
      if (response.ok) {
        await fetchRuggedProjects();
        setShowAddRuggedModal(false);
        setNewRuggedProject({ policy_id: '', name: '', description: '', image_url: '', rug_date: '', estimated_holders: '' });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add project');
      }
    } catch (error) {
      alert('Failed to add project');
    }
  };

  const deleteRuggedProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to remove this project?')) return;
    
    try {
      const response = await fetch(`${API}/rugged-projects/${projectId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchRuggedProjects();
      }
    } catch (error) {
      alert('Failed to remove project');
    }
  };

  const fetchVersionInfo = async () => {
    try {
      const response = await fetch(`${API}/system/version`);
      if (response.ok) {
        const data = await response.json();
        setVersionInfo(data);
      }
    } catch (error) {
    }
  };

  const fetchBackups = async () => {
    try {
      const response = await fetch(`${API}/system/backups`);
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      }
    } catch (error) {
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    setUpdateLog([{ time: new Date().toISOString(), message: "Starting update from GitHub...", type: "info" }]);
    
    try {
      const response = await fetch(`${API}/system/update`, { method: 'POST' });
      const data = await response.json();
      
      if (response.ok) {
        setUpdateLog(prev => [
          ...prev,
          { time: new Date().toISOString(), message: "Pulling latest changes...", type: "info" },
          { time: new Date().toISOString(), message: data.message || "Update completed successfully!", type: "success" },
        ]);
        // Refresh version info
        await fetchVersionInfo();
      } else {
        setUpdateLog(prev => [
          ...prev,
          { time: new Date().toISOString(), message: data.error || "Update failed", type: "error" },
        ]);
      }
    } catch (error) {
      setUpdateLog(prev => [
        ...prev,
        { time: new Date().toISOString(), message: `Error: ${error.message}`, type: "error" },
      ]);
    }
    
    setUpdating(false);
  };

  const handleBackup = async () => {
    setBackingUp(true);
    
    try {
      const response = await fetch(`${API}/system/backup`, { method: 'POST' });
      const data = await response.json();
      
      if (response.ok) {
        // Refresh backup list
        await fetchBackups();
        alert("Backup created successfully!");
      } else {
        alert(data.error || "Backup failed");
      }
    } catch (error) {
      alert(`Backup error: ${error.message}`);
    }
    
    setBackingUp(false);
  };

  const handleDownloadBackup = async (backupName) => {
    try {
      window.open(`${API}/system/backups/${backupName}/download`, '_blank');
    } catch (error) {
      alert(`Download error: ${error.message}`);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const updateMilestone = (tier, field, value) => {
    setSettings(prev => ({
      ...prev,
      milestones: {
        ...prev.milestones,
        [tier]: { ...prev.milestones[tier], [field]: value }
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    setHasChanges(false);
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasChanges(false);
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "verifications", label: "Verifications", icon: ClipboardCheck },
    { id: "teams", label: "Team Cards", icon: Shield },
    { id: "campaigns", label: "Campaigns", icon: Target },
    { id: "tokenomics", label: "Tokenomics", icon: Coins },
    { id: "dumpzone", label: "Dump Zone", icon: Trash2 },
    { id: "rugged", label: "Rugged Projects", icon: AlertTriangle },
    { id: "advertising", label: "Advertising", icon: Flame },
    { id: "partners", label: "Partners", icon: Rocket },
    { id: "users", label: "Users & Roles", icon: Users },
    { id: "milestones", label: "Milestones", icon: Award },
    { id: "limits", label: "Limits", icon: Shield },
    { id: "theme", label: "Theme", icon: Palette },
    { id: "system", label: "System", icon: Github },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Header */}
      <header className="h-16 bg-[#0D0D0D] border-b border-white/5 flex items-center justify-between px-6 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/app')}
            className="text-white/50 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to App
          </Button>
          <Separator orientation="vertical" className="h-6 bg-white/10" />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Command Center</h1>
              <p className="text-xs text-white/50">Ecosystem Control Panel</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <Lock className="w-3 h-3 mr-1" />
            Superadmin
          </Badge>
          {hasChanges && (
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Unsaved Changes
            </Badge>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleReset}
            className="border-white/10 text-white/50 hover:text-white"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Tabs */}
        <aside className="w-56 bg-[#0D0D0D] border-r border-white/5 min-h-[calc(100vh-4rem)] p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                    isActive 
                      ? "bg-orange-500/20 text-orange-400" 
                      : "text-white/50 hover:bg-white/5 hover:text-white/80"
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Quick Stats */}
          <div className="mt-8 p-4 rounded-xl bg-gradient-to-br from-orange-500/10 to-purple-500/10 border border-white/5">
            <p className="text-xs text-white/50 mb-3">Live Stats</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-white/50">Users</span>
                <span className="text-xs text-white font-mono">{DEMO.stats.total_users.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-white/50">NFTs Dumped</span>
                <span className="text-xs text-white font-mono">{DEMO.stats.total_nfts_dumped.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-white/50">BURNZ Burned</span>
                <span className="text-xs text-orange-400 font-mono">{DEMO.stats.total_burnz_burned.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {activeTab === "overview" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Platform Overview</h2>
                <p className="text-white/50 text-sm">Real-time ecosystem metrics and health</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4">
                <StatCard label="Total Users" value={DEMO.stats.total_users.toLocaleString()} icon={Users} color="purple" change={12} />
                <StatCard label="Active Pools" value={DEMO.stats.total_pools} icon={Flame} color="orange" change={3} />
                <StatCard label="NFTs Dumped" value={DEMO.stats.total_nfts_dumped.toLocaleString()} icon={Trash2} color="cyan" change={8} />
                <StatCard label="BURNZ Burned" value={`${(DEMO.stats.total_burnz_burned / 1000).toFixed(0)}K`} icon={Flame} color="red" change={15} />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <StatCard label="Quest Completions" value={DEMO.stats.total_quest_completions.toLocaleString()} icon={Target} color="green" change={22} />
                <StatCard label="Active Partners" value={DEMO.stats.partner_projects} icon={Rocket} color="yellow" change={2} />
                <StatCard label="Conversion Rate" value={`${settings.xpToBurnzRatio}:1`} icon={TrendingUp} color="purple" />
                <StatCard label="Claim Fee" value={`${settings.claimBurnFee}%`} icon={Percent} color="orange" />
              </div>

              {/* Current Settings Summary */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4 text-purple-400" />
                    Current Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs text-white/50 mb-2">Tokenomics</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">XP:BURNZ Ratio</span>
                          <span className="text-white font-mono">{settings.xpToBurnzRatio}:1</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Min Claim</span>
                          <span className="text-white font-mono">{settings.minClaimThreshold} BURNZ</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Claim Fee</span>
                          <span className="text-white font-mono">{settings.claimBurnFee}%</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-2">Dump Zone</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">XP per Dump</span>
                          <span className="text-white font-mono">{settings.xpPerDump} XP</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Burn per Dump</span>
                          <span className="text-white font-mono">{settings.burnzBurnPerDump} BURNZ</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Max per Dump</span>
                          <span className="text-white font-mono">{settings.maxNftsPerDump} NFTs</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-2">Partners</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Fee Burn</span>
                          <span className="text-white font-mono">{settings.partnerFeeBurnPercent}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Self-Serve</span>
                          <span className="text-white font-mono">{settings.selfServePriceAda} ADA</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-white/70">Managed</span>
                          <span className="text-white font-mono">{settings.managedServicePrice} ADA</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "tokenomics" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Token Economics</h2>
                <p className="text-white/50 text-sm">Control BURNZ token conversion and claiming mechanics</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <SettingCard 
                  icon={TrendingUp} 
                  title="XP to BURNZ Conversion" 
                  description="Set the ratio for converting XP to BURNZ tokens"
                  color="orange"
                >
                  <InputField 
                    label="Conversion Ratio (XP ÷ X = BURNZ)" 
                    value={settings.xpToBurnzRatio}
                    onChange={(v) => updateSetting('xpToBurnzRatio', v)}
                    min={1}
                    max={100}
                  />
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <p className="text-xs text-orange-400">
                      Example: 1000 XP ÷ {settings.xpToBurnzRatio} = {(1000 / settings.xpToBurnzRatio).toFixed(0)} BURNZ
                    </p>
                  </div>
                </SettingCard>

                <SettingCard 
                  icon={Gift} 
                  title="Claiming Mechanics" 
                  description="Set minimum threshold and burn fees for claims"
                  color="purple"
                >
                  <InputField 
                    label="Minimum Claim Threshold" 
                    value={settings.minClaimThreshold}
                    onChange={(v) => updateSetting('minClaimThreshold', v)}
                    suffix="BURNZ"
                    min={1}
                    max={1000}
                  />
                  <InputField 
                    label="Claim Burn Fee" 
                    value={settings.claimBurnFee}
                    onChange={(v) => updateSetting('claimBurnFee', v)}
                    suffix="%"
                    min={0}
                    max={50}
                    step={0.5}
                  />
                  <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <p className="text-xs text-purple-400">
                      Claiming {settings.minClaimThreshold} BURNZ burns {(settings.minClaimThreshold * settings.claimBurnFee / 100).toFixed(1)} BURNZ
                    </p>
                  </div>
                </SettingCard>
              </div>

              {/* Deflationary Mechanics Summary */}
              <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/20">
                <CardHeader>
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4 text-red-400" />
                    Deflationary Burn Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 rounded-lg bg-black/20">
                      <p className="text-2xl font-bold text-red-400">{settings.burnzBurnPerDump}</p>
                      <p className="text-xs text-white/50">BURNZ/Dump</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-black/20">
                      <p className="text-2xl font-bold text-red-400">{settings.claimBurnFee}%</p>
                      <p className="text-xs text-white/50">Claim Fee</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-black/20">
                      <p className="text-2xl font-bold text-red-400">{settings.partnerFeeBurnPercent}%</p>
                      <p className="text-xs text-white/50">Partner Fee</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-black/20">
                      <p className="text-2xl font-bold text-red-400">{settings.featuredPlacementCost}</p>
                      <p className="text-xs text-white/50">Featured Cost</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Token Burn Wallets */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Token Burning System
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    Two-wallet system for transparent token burns
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Team Wallet */}
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Coins className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-blue-400 font-semibold">Team Wallet</p>
                          <p className="text-white/30 text-xs">Source of tokens for burns</p>
                        </div>
                      </div>
                      <code className="text-white/70 text-xs block break-all bg-black/30 p-2 rounded">
                        addr1qxc6u2d5ppeuhg7tkj5quf3csxp2022g98az65hpad9m6yreuxes6p3hhg880tegsg95xqwek6srgac5jq04x9rcm2qq6a6md7
                      </code>
                      <p className="text-white/30 text-xs mt-2">
                        Tokens are pulled from here during transactions
                      </p>
                    </div>

                    {/* Burn Wallet */}
                    <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <Flame className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                          <p className="text-orange-400 font-semibold">Final Burn Wallet</p>
                          <p className="text-white/30 text-xs">Monthly burn destination</p>
                        </div>
                      </div>
                      <code className="text-white/70 text-xs block break-all bg-black/30 p-2 rounded">
                        addr1q90dycz89a0rv607nvw555d4d2q3ue04l247f3efm3vrwcvq8rpejvwgced79s47u2r22xtqkywl6nn9f8wtck2zzvssa2jsg2
                      </code>
                      <p className="text-white/30 text-xs mt-2">
                        All tokens here are burned at end of each month
                      </p>
                    </div>
                  </div>

                  {/* Burn Flow Diagram */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-white font-semibold mb-3">Burn Flow</p>
                    <div className="flex items-center justify-between text-center">
                      <div className="flex-1 p-3 rounded-lg bg-blue-500/10">
                        <p className="text-blue-400 text-sm font-medium">Team Wallet</p>
                        <p className="text-white/30 text-xs">$BURNZ Reserve</p>
                      </div>
                      <div className="px-4">
                        <div className="text-white/50">→</div>
                        <p className="text-white/30 text-xs">On Transaction</p>
                      </div>
                      <div className="flex-1 p-3 rounded-lg bg-orange-500/10">
                        <p className="text-orange-400 text-sm font-medium">Burn Wallet</p>
                        <p className="text-white/30 text-xs">Pending Burn</p>
                      </div>
                      <div className="px-4">
                        <div className="text-white/50">→</div>
                        <p className="text-white/30 text-xs">Monthly</p>
                      </div>
                      <div className="flex-1 p-3 rounded-lg bg-red-500/10">
                        <p className="text-red-400 text-sm font-medium">🔥 BURNED</p>
                        <p className="text-white/30 text-xs">Forever Gone</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Burn Report with Chart */}
              <BurnTracker compact={false} />
            </motion.div>
          )}

          {activeTab === "dumpzone" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Dump Zone Settings</h2>
                <p className="text-white/50 text-sm">Configure NFT dumping rewards and limits</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <SettingCard 
                  icon={Zap} 
                  title="Dump Rewards" 
                  description="XP earned when users dump NFTs"
                  color="cyan"
                >
                  <InputField 
                    label="XP per NFT Dump" 
                    value={settings.xpPerDump}
                    onChange={(v) => updateSetting('xpPerDump', v)}
                    suffix="XP"
                    min={1}
                    max={1000}
                  />
                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                    <p className="text-xs text-cyan-400">
                      Dumping 10 NFTs = {settings.xpPerDump * 10} XP = {(settings.xpPerDump * 10 / settings.xpToBurnzRatio).toFixed(0)} BURNZ
                    </p>
                  </div>
                </SettingCard>

                <SettingCard 
                  icon={Flame} 
                  title="Dump Burns" 
                  description="BURNZ burned per dump to maintain deflation"
                  color="red"
                >
                  <InputField 
                    label="BURNZ Burned per Dump" 
                    value={settings.burnzBurnPerDump}
                    onChange={(v) => updateSetting('burnzBurnPerDump', v)}
                    suffix="BURNZ"
                    min={0}
                    max={100}
                  />
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400">
                      10 dumps = {settings.burnzBurnPerDump * 10} BURNZ permanently burned 🔥
                    </p>
                  </div>
                </SettingCard>

                <SettingCard 
                  icon={Shield} 
                  title="Dump Limits" 
                  description="Maximum NFTs per single dump transaction"
                  color="purple"
                >
                  <InputField 
                    label="Max NFTs per Dump" 
                    value={settings.maxNftsPerDump}
                    onChange={(v) => updateSetting('maxNftsPerDump', v)}
                    suffix="NFTs"
                    min={1}
                    max={200}
                  />
                </SettingCard>
              </div>
            </motion.div>
          )}

          {/* Rugged Projects Management Tab */}
          {activeTab === "rugged" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Rugged Projects</h2>
                  <p className="text-white/50 text-sm">Manage policy IDs for rugged NFT projects that can be dumped</p>
                </div>
                <Button 
                  onClick={() => setShowAddRuggedModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />Add Project
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-[#1A1A1A] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-orange-400">{ruggedProjects.length}</p>
                    <p className="text-white/50 text-sm">Active Projects</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#1A1A1A] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-white">
                      {ruggedProjects.reduce((acc, p) => acc + (p.estimated_holders || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-white/50 text-sm">Est. Total Holders</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#1A1A1A] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">Live</p>
                    <p className="text-white/50 text-sm">Filter Status</p>
                  </CardContent>
                </Card>
              </div>

              {/* Projects List */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                    Registered Policy IDs
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    NFTs from these policy IDs will appear in the Dump Zone for users
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingRugged ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-6 h-6 text-white/50 animate-spin mx-auto" />
                      <p className="text-white/50 mt-2">Loading projects...</p>
                    </div>
                  ) : ruggedProjects.length === 0 ? (
                    <div className="text-center py-8 text-white/50">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No rugged projects added yet</p>
                      <p className="text-sm mt-1">Add policy IDs to enable NFT filtering</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {ruggedProjects.map(project => (
                        <div 
                          key={project.id}
                          className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/30 transition-all"
                        >
                          {project.image_url ? (
                            <img 
                              src={project.image_url} 
                              alt={project.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                              <Trash2 className="w-5 h-5 text-orange-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold">{project.name}</p>
                            <p className="text-white/50 text-xs font-mono truncate">{project.policy_id}</p>
                            {project.description && (
                              <p className="text-white/30 text-xs mt-1 truncate">{project.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {project.estimated_holders && (
                              <p className="text-white/70 text-sm">{project.estimated_holders.toLocaleString()} holders</p>
                            )}
                            {project.rug_date && (
                              <p className="text-white/30 text-xs">Rugged: {project.rug_date}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteRuggedProject(project.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add Project Modal */}
              {showAddRuggedModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <Card className="bg-[#1A1A1A] border-white/10 w-full max-w-lg">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Plus className="w-5 h-5 text-orange-400" />
                        Add Rugged Project
                      </CardTitle>
                      <CardDescription className="text-white/50">
                        Enter the policy ID and details of the rugged NFT project
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="text-white/70 text-sm block mb-2">Policy ID *</label>
                        <Input
                          placeholder="56 character policy ID"
                          value={newRuggedProject.policy_id}
                          onChange={(e) => setNewRuggedProject(prev => ({ ...prev, policy_id: e.target.value }))}
                          className="bg-black/30 border-white/10 text-white font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-white/70 text-sm block mb-2">Project Name *</label>
                        <Input
                          placeholder="e.g., Rugged Apes"
                          value={newRuggedProject.name}
                          onChange={(e) => setNewRuggedProject(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-black/30 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-white/70 text-sm block mb-2">Description</label>
                        <Input
                          placeholder="Brief description of the project"
                          value={newRuggedProject.description}
                          onChange={(e) => setNewRuggedProject(prev => ({ ...prev, description: e.target.value }))}
                          className="bg-black/30 border-white/10 text-white"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-white/70 text-sm block mb-2">Image URL</label>
                          <Input
                            placeholder="https://..."
                            value={newRuggedProject.image_url}
                            onChange={(e) => setNewRuggedProject(prev => ({ ...prev, image_url: e.target.value }))}
                            className="bg-black/30 border-white/10 text-white"
                          />
                        </div>
                        <div>
                          <label className="text-white/70 text-sm block mb-2">Est. Holders</label>
                          <Input
                            type="number"
                            placeholder="e.g., 5000"
                            value={newRuggedProject.estimated_holders}
                            onChange={(e) => setNewRuggedProject(prev => ({ ...prev, estimated_holders: e.target.value }))}
                            className="bg-black/30 border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-white/70 text-sm block mb-2">Rug Date</label>
                        <Input
                          type="date"
                          value={newRuggedProject.rug_date}
                          onChange={(e) => setNewRuggedProject(prev => ({ ...prev, rug_date: e.target.value }))}
                          className="bg-black/30 border-white/10 text-white"
                        />
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button
                          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={addRuggedProject}
                        >
                          <Plus className="w-4 h-4 mr-2" />Add Project
                        </Button>
                        <Button
                          variant="outline"
                          className="border-white/20 text-white"
                          onClick={() => setShowAddRuggedModal(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </motion.div>
          )}

          {/* Verifications Tab - Quest Proof Review */}
          {activeTab === "verifications" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Quest Verification Queue</h2>
                <p className="text-white/50 text-sm">Review and approve user-submitted proof for quest completion</p>
              </div>

              <VerificationQueue />
            </motion.div>
          )}

          {activeTab === "teams" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Team Cards Management</h2>
                <p className="text-white/50 text-sm">Create and manage Team Cards for competitive user grouping</p>
              </div>

              {/* Team Management Section */}
              <TeamManagementSection />

              <Separator className="bg-white/10" />

              {/* Team Leaderboard Settings */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    Team Leaderboard Settings
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    Configure how teams are ranked and rewarded
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <p className="text-white/50 text-sm mb-2">Primary Metric</p>
                      <p className="text-white font-bold">Total Team XP</p>
                      <p className="text-white/30 text-xs mt-1">Sum of all member XP earnings</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <p className="text-white/50 text-sm mb-2">Secondary Metric</p>
                      <p className="text-white font-bold">BURNZ Burned</p>
                      <p className="text-white/30 text-xs mt-1">Total BURNZ burned by team members</p>
                    </div>
                  </div>
                  <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <p className="text-purple-400 text-sm">
                      🏆 Season Rewards: Top 3 teams receive bonus XP and exclusive badges at season end.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "campaigns" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Campaign Management</h2>
                <p className="text-white/50 text-sm">Create and manage Web2-friendly marketing campaigns for partners</p>
              </div>

              {/* Campaign Creation Section */}
              <CampaignCreationSection />

              <Separator className="bg-white/10" />

              {/* Campaign Types Overview */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Campaign Types</CardTitle>
                  <CardDescription className="text-white/50">5 simple campaign types for partner marketing</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-3">
                    {CAMPAIGN_TYPES.map((type) => (
                      <div key={type.id} className={`p-4 rounded-lg border border-${type.color}-500/20 bg-${type.color}-500/10 text-center`}>
                        <span className="text-3xl block mb-2">{type.icon}</span>
                        <p className="text-white font-medium text-sm">{type.label}</p>
                        <p className="text-white/40 text-xs mt-1">{type.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Separator className="bg-white/10" />

              <div>
                <h3 className="text-lg font-bold text-white mb-4">Campaign Settings</h3>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <SettingCard 
                  icon={Zap} 
                  title="XP Multiplier" 
                  description="Adjust XP rewards for all campaigns"
                  color="orange"
                >
                  <InputField 
                    label="Campaign XP Multiplier" 
                    value={settings.questXpMultiplier}
                    onChange={(v) => updateSetting('questXpMultiplier', v)}
                    suffix="x"
                    min={0.1}
                    max={10}
                    step={0.1}
                  />
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <p className="text-xs text-orange-400">
                      Base 500 XP campaign = {(500 * settings.questXpMultiplier).toFixed(0)} XP with multiplier
                    </p>
                  </div>
                </SettingCard>

                <SettingCard 
                  icon={Timer} 
                  title="Daily Limits" 
                  description="Maximum daily campaign completions per user"
                  color="cyan"
                >
                  <InputField 
                    label="Daily Campaign Limit" 
                    value={settings.dailyQuestLimit}
                    onChange={(v) => updateSetting('dailyQuestLimit', v)}
                    suffix="campaigns"
                    min={1}
                    max={20}
                  />
                </SettingCard>
              </div>
            </motion.div>
          )}

          {activeTab === "advertising" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">FireBoard Advertising</h2>
                <p className="text-white/50 text-sm">Manage the dashboard advertising carousel - images, videos, and promotional content</p>
              </div>

              {/* FireBoard Ads Management */}
              <FireBoardAdsSection />

              <Separator className="bg-white/10" />

              <div>
                <h3 className="text-lg font-bold text-white mb-4">Advertising Revenue</h3>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gray-500/10 border-gray-500/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-gray-400 text-xs mb-1">Standard Tier</p>
                    <p className="text-2xl font-bold text-white">500K</p>
                    <p className="text-gray-500 text-xs">BURNZ / week</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-500/10 border-orange-500/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-orange-400 text-xs mb-1">Featured Tier</p>
                    <p className="text-2xl font-bold text-white">1M</p>
                    <p className="text-orange-500 text-xs">BURNZ / 2 weeks</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-500/10 border-purple-500/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-purple-400 text-xs mb-1">Premium Tier</p>
                    <p className="text-2xl font-bold text-white">2.5M</p>
                    <p className="text-purple-500 text-xs">BURNZ / month</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-[#1A1A1A] border-white/10">
                <CardContent className="p-4">
                  <h4 className="text-white font-medium mb-3">Advertising Guidelines</h4>
                  <ul className="space-y-2 text-white/50 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Images should be 800x400px (16:7 aspect ratio) for best display</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Videos must use YouTube embed format (youtube.com/embed/...)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Ads rotate automatically every 8 seconds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Premium ads get priority placement and longer display time</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "partners" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Partner Settings</h2>
                <p className="text-white/50 text-sm">Configure partner pricing and fee structures</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <SettingCard 
                  icon={DollarSign} 
                  title="Self-Serve Pricing" 
                  description="Price for partners to create their own quests"
                  color="green"
                >
                  <InputField 
                    label="Price in ADA" 
                    value={settings.selfServePriceAda}
                    onChange={(v) => updateSetting('selfServePriceAda', v)}
                    suffix="ADA"
                    min={1}
                    max={1000}
                  />
                  <InputField 
                    label="Price in BURNZ" 
                    value={settings.selfServePriceBurnz}
                    onChange={(v) => updateSetting('selfServePriceBurnz', v)}
                    suffix="BURNZ"
                    min={1}
                    max={10000}
                  />
                </SettingCard>

                <SettingCard 
                  icon={Crown} 
                  title="Managed Service Pricing" 
                  description="Price for full-service campaign management"
                  color="yellow"
                >
                  <InputField 
                    label="Managed Service Price" 
                    value={settings.managedServicePrice}
                    onChange={(v) => updateSetting('managedServicePrice', v)}
                    suffix="ADA"
                    min={1}
                    max={5000}
                  />
                  <InputField 
                    label="Featured Placement" 
                    value={settings.featuredPlacementCost}
                    onChange={(v) => updateSetting('featuredPlacementCost', v)}
                    suffix="BURNZ"
                    min={1}
                    max={1000}
                  />
                </SettingCard>

                <SettingCard 
                  icon={Flame} 
                  title="Fee Burns" 
                  description="Percentage of partner fees burned"
                  color="red"
                >
                  <InputField 
                    label="Partner Fee Burn %" 
                    value={settings.partnerFeeBurnPercent}
                    onChange={(v) => updateSetting('partnerFeeBurnPercent', v)}
                    suffix="%"
                    min={0}
                    max={100}
                  />
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400">
                      {settings.selfServePriceBurnz} BURNZ payment burns {(settings.selfServePriceBurnz * settings.partnerFeeBurnPercent / 100).toFixed(0)} BURNZ
                    </p>
                  </div>
                </SettingCard>
              </div>

              <Separator className="bg-white/10" />

              {/* Partner Applications Section */}
              <PartnerApplicationsSection />
            </motion.div>
          )}

          {activeTab === "users" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Users & Role Management</h2>
                <p className="text-white/50 text-sm">Manage team members and their access levels</p>
              </div>

              {/* Roles Overview */}
              <UserRolesSection />
            </motion.div>
          )}

          {activeTab === "milestones" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Milestone Thresholds</h2>
                <p className="text-white/50 text-sm">Configure XP requirements and rewards for each tier</p>
              </div>

              <div className="space-y-4">
                {Object.entries(settings.milestones).map(([tier, values]) => {
                  const icons = { bronze: "🥉", silver: "🥈", gold: "🥇", diamond: "💎", legendary: "👑" };
                  const colors = { bronze: "orange", silver: "gray", gold: "yellow", diamond: "cyan", legendary: "purple" };
                  return (
                    <Card key={tier} className="bg-[#1A1A1A] border-white/10">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl bg-${colors[tier]}-500/20 flex items-center justify-center text-2xl`}>
                            {icons[tier]}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-semibold capitalize">{tier} Milestone</h3>
                            <p className="text-xs text-white/50">Configure threshold and rewards</p>
                          </div>
                          <div className="flex items-center gap-6">
                            <div>
                              <label className="text-xs text-white/50 block mb-1">XP Required</label>
                              <Input
                                type="number"
                                value={values.xp}
                                onChange={(e) => updateMilestone(tier, 'xp', parseInt(e.target.value) || 0)}
                                className="w-32 bg-[#0D0D0D] border-white/10 text-white text-right h-9"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-white/50 block mb-1">UEX Reward</label>
                              <Input
                                type="number"
                                value={values.uex}
                                onChange={(e) => updateMilestone(tier, 'uex', parseInt(e.target.value) || 0)}
                                className="w-24 bg-[#0D0D0D] border-white/10 text-white text-right h-9"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-white/50 block mb-1">UEXC Reward</label>
                              <Input
                                type="number"
                                value={values.uexc}
                                onChange={(e) => updateMilestone(tier, 'uexc', parseInt(e.target.value) || 0)}
                                className="w-24 bg-[#0D0D0D] border-white/10 text-white text-right h-9"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {activeTab === "limits" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Platform Limits</h2>
                <p className="text-white/50 text-sm">Configure energy, vesting, and other platform limits</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <SettingCard 
                  icon={Zap} 
                  title="Energy System" 
                  description="Configure energy limits and regeneration"
                  color="yellow"
                >
                  <InputField 
                    label="Maximum Energy" 
                    value={settings.maxEnergy}
                    onChange={(v) => updateSetting('maxEnergy', v)}
                    min={50}
                    max={1000}
                  />
                  <InputField 
                    label="Energy Regen Rate" 
                    value={settings.energyRegenRate}
                    onChange={(v) => updateSetting('energyRegenRate', v)}
                    suffix="/hr"
                    min={1}
                    max={100}
                  />
                </SettingCard>

                <SettingCard 
                  icon={Timer} 
                  title="Vesting Schedule" 
                  description="UEX token vesting period"
                  color="purple"
                >
                  <InputField 
                    label="Vesting Period" 
                    value={settings.vestingPeriodMonths}
                    onChange={(v) => updateSetting('vestingPeriodMonths', v)}
                    suffix="months"
                    min={1}
                    max={48}
                  />
                  <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <p className="text-xs text-purple-400">
                      UEX tokens vest linearly over {settings.vestingPeriodMonths} months
                    </p>
                  </div>
                </SettingCard>
              </div>
            </motion.div>
          )}

          {/* Theme Tab */}
          {activeTab === "theme" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Theme Customization</h2>
                <p className="text-white/50 text-sm">Customize the look and feel of your platform</p>
              </div>
              
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardContent className="pt-6">
                  <ThemeSelector />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* System Tab - Version, Updates & Backups */}
          {activeTab === "system" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-xl font-bold text-white mb-1">System Administration</h2>
                <p className="text-white/50 text-sm">Version control, updates, and database backups</p>
              </div>

              {/* Version & Update Section */}
              <div className="grid grid-cols-2 gap-6">
                {/* Current Version Card */}
                <Card className="bg-[#1A1A1A] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Github className="w-5 h-5 text-purple-400" />
                      Current Version
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50">Version</span>
                      <Badge className="bg-green-500/20 text-green-400 border-0 text-lg px-3">
                        v{versionInfo.version}
                      </Badge>
                    </div>
                    <Separator className="bg-white/10" />
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/50">Branch</span>
                        <span className="text-white font-mono">{versionInfo.branch}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Commit</span>
                        <span className="text-cyan-400 font-mono">{versionInfo.commit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Last Update</span>
                        <span className="text-white">{new Date(versionInfo.lastUpdate).toLocaleString()}</span>
                      </div>
                    </div>
                    <Separator className="bg-white/10" />
                    <a 
                      href={versionInfo.repo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-purple-400 hover:text-purple-300 text-sm"
                    >
                      <Github className="w-4 h-4" />
                      View on GitHub
                    </a>
                  </CardContent>
                </Card>

                {/* Update Control Card */}
                <Card className="bg-[#1A1A1A] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Download className="w-5 h-5 text-orange-400" />
                      Update Application
                    </CardTitle>
                    <CardDescription className="text-white/50">
                      Pull latest changes from GitHub repository
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                      <p className="text-orange-400 text-sm font-medium mb-2">Repository</p>
                      <p className="text-white font-mono text-xs break-all">{versionInfo.repo}</p>
                    </div>
                    
                    <Button 
                      onClick={handleUpdate}
                      disabled={updating}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12"
                    >
                      {updating ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Pull Latest Changes
                        </>
                      )}
                    </Button>

                    {/* Update Log */}
                    {updateLog.length > 0 && (
                      <div className="mt-4 p-3 rounded-lg bg-black/30 border border-white/5">
                        <p className="text-white/50 text-xs mb-2 flex items-center gap-1">
                          <Terminal className="w-3 h-3" /> Update Log
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {updateLog.map((log, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs">
                              {log.type === "success" ? (
                                <CheckCircle className="w-3 h-3 text-green-400 mt-0.5" />
                              ) : log.type === "error" ? (
                                <XCircle className="w-3 h-3 text-red-400 mt-0.5" />
                              ) : (
                                <Clock className="w-3 h-3 text-blue-400 mt-0.5" />
                              )}
                              <span className={`${log.type === "success" ? "text-green-400" : log.type === "error" ? "text-red-400" : "text-blue-400"}`}>
                                {log.message}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Database Backup Section */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Database className="w-5 h-5 text-cyan-400" />
                        Database Backups
                      </CardTitle>
                      <CardDescription className="text-white/50">
                        Backup Supabase data locally
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={handleBackup}
                      disabled={backingUp}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                      {backingUp ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Backing Up...
                        </>
                      ) : (
                        <>
                          <HardDrive className="w-4 h-4 mr-2" />
                          Create Backup
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {backups.length === 0 ? (
                      <div className="text-center py-8 text-white/30">
                        <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No backups yet. Create your first backup above.</p>
                      </div>
                    ) : (
                      backups.map((backup) => (
                        <div 
                          key={backup.id} 
                          className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{backup.name}</p>
                              <p className="text-white/40 text-xs">{backup.date} • {backup.tables} tables</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-white/50 text-sm">{backup.size}</span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadBackup(backup.name)}
                              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Changelog Section */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-purple-400" />
                    Changelog
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    Recent updates and changes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {changelog.map((release, i) => (
                      <div key={i} className="relative pl-6 pb-4 border-l-2 border-purple-500/30 last:border-0 last:pb-0">
                        <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-purple-500 border-4 border-[#1A1A1A]" />
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className="bg-purple-500/20 text-purple-400 border-0">
                            v{release.version}
                          </Badge>
                          <span className="text-white/40 text-sm">{release.date}</span>
                        </div>
                        <ul className="space-y-1">
                          {release.changes.map((change, j) => (
                            <li key={j} className="text-white/70 text-sm flex items-center gap-2">
                              <Check className="w-3 h-3 text-green-400" />
                              {change}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* System Settings Section - XP, Pricing, Wallets */}
              <Card className="bg-[#1A1A1A] border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Settings className="w-5 h-5 text-yellow-400" />
                        System Settings
                      </CardTitle>
                      <CardDescription className="text-white/50">
                        Configure XP rewards, partner pricing, and wallet addresses
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={saveSystemSettings}
                      disabled={savingSystemSettings}
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      {savingSystemSettings ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* XP Rewards Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-white/5">
                      <label className="text-white/50 text-sm block mb-2">Referral XP Reward</label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={systemSettings.referral_xp_reward}
                          onChange={(e) => setSystemSettings(prev => ({
                            ...prev,
                            referral_xp_reward: parseInt(e.target.value) || 0
                          }))}
                          className="bg-black/30 border-white/10 text-white"
                        />
                        <span className="text-white/50 text-sm">XP</span>
                      </div>
                      <p className="text-white/30 text-xs mt-1">XP given to referrer when someone uses their code</p>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-white/5">
                      <label className="text-white/50 text-sm block mb-2">NFT Dump XP (per NFT)</label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={systemSettings.nft_dump_xp_per_nft}
                          onChange={(e) => setSystemSettings(prev => ({
                            ...prev,
                            nft_dump_xp_per_nft: parseInt(e.target.value) || 0
                          }))}
                          className="bg-black/30 border-white/10 text-white"
                        />
                        <span className="text-white/50 text-sm">XP</span>
                      </div>
                      <p className="text-white/30 text-xs mt-1">XP awarded for each NFT dumped</p>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-white/5">
                      <label className="text-white/50 text-sm block mb-2">Min ADA for Dump</label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={systemSettings.nft_dump_min_ada}
                          onChange={(e) => setSystemSettings(prev => ({
                            ...prev,
                            nft_dump_min_ada: parseInt(e.target.value) || 0
                          }))}
                          className="bg-black/30 border-white/10 text-white"
                        />
                        <span className="text-white/50 text-sm">ADA</span>
                      </div>
                      <p className="text-white/30 text-xs mt-1">Minimum ADA users must include when dumping</p>
                    </div>
                  </div>

                  <Separator className="bg-white/10" />

                  {/* Partner Pricing */}
                  <div>
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      Partner Tier Pricing (ADA)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-xl bg-gray-500/10 border border-gray-500/20">
                        <label className="text-gray-400 text-sm block mb-2">Starter Tier</label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={systemSettings.partner_pricing?.starter?.ada || 500}
                            onChange={(e) => setSystemSettings(prev => ({
                              ...prev,
                              partner_pricing: {
                                ...prev.partner_pricing,
                                starter: { ...prev.partner_pricing?.starter, ada: parseInt(e.target.value) || 0 }
                              }
                            }))}
                            className="bg-black/30 border-white/10 text-white"
                          />
                          <span className="text-white/50 text-sm">ADA</span>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                        <label className="text-orange-400 text-sm block mb-2">Growth Tier ⭐</label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={systemSettings.partner_pricing?.growth?.ada || 1500}
                            onChange={(e) => setSystemSettings(prev => ({
                              ...prev,
                              partner_pricing: {
                                ...prev.partner_pricing,
                                growth: { ...prev.partner_pricing?.growth, ada: parseInt(e.target.value) || 0 }
                              }
                            }))}
                            className="bg-black/30 border-white/10 text-white"
                          />
                          <span className="text-white/50 text-sm">ADA</span>
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                        <label className="text-purple-400 text-sm block mb-2">Enterprise Tier</label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={systemSettings.partner_pricing?.enterprise?.ada || 5000}
                            onChange={(e) => setSystemSettings(prev => ({
                              ...prev,
                              partner_pricing: {
                                ...prev.partner_pricing,
                                enterprise: { ...prev.partner_pricing?.enterprise, ada: parseInt(e.target.value) || 0 }
                              }
                            }))}
                            className="bg-black/30 border-white/10 text-white"
                          />
                          <span className="text-white/50 text-sm">ADA</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-white/10" />

                  {/* Wallet Addresses */}
                  <div>
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-blue-400" />
                      Wallet Addresses (Read Only)
                    </h3>
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                        <label className="text-orange-400 text-sm block mb-2">NFT Dump Wallet</label>
                        <code className="text-white text-xs block break-all bg-black/30 p-3 rounded-lg">
                          {walletConfig.nft_dump_wallet || 'Not configured'}
                        </code>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                        <label className="text-green-400 text-sm block mb-2">Partner Payment Wallet</label>
                        <code className="text-white text-xs block break-all bg-black/30 p-3 rounded-lg">
                          {walletConfig.partner_payment_wallet || 'Not configured'}
                        </code>
                      </div>
                    </div>
                    <p className="text-white/30 text-xs mt-3">
                      Wallet addresses are configured in the backend environment variables
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CommandCenter;
